<template>
  <div :id="viewId">
    <v-card
      class="pa-0"
      elevation="0"
      color="light"
      style="z-index: 2"
    >
      <v-progress-linear
        indeterminate
        v-if="processing"
      />
    </v-card>
    <v-list class="pa-0">
      <v-toolbar
        class="fixed-bar"
        flat
        v-if="showSearch"
      >
        <v-text-field
          v-model="actionListQuery"
          class="grow mr-2"
          outlined
          hide-details
          dense
          filled
          :placeholder="$t('search')"
          clearable
          @input="onSearchInput"
        />
        <v-btn
          v-if="refreshable && loadItemsCallback !== null && loadItemsCallback !== undefined"
          icon
          small
          outlined
          @click="refreshItems"
          class="mr-1 ml-2 grey lighten-2"
          dark
        >
          <v-icon
            small
            color="grey darken-2"
          >
            mdi-sync
          </v-icon>
        </v-btn>
        <v-btn
          v-if="Array.isArray(actions) && actions.length > 0"
          icon
          small
          outlined
          @click="showBulkActions = !showBulkActions"
          :disabled="false"
          :loading="false"
          class="mr-1 grey lighten-2"
          dark
        >
          <v-icon
            small
            color="grey darken-2"
          >
            mdi-run-fast
          </v-icon>
        </v-btn>
      </v-toolbar>
      <div
        v-if="Array.isArray(callToActions) && callToActions.length > 0"
        class="mx-4"
      >
        <template v-for="(callToAction, index) in callToActions">
          <v-btn
            :key="index"
            block
            @click="callToAction.callback"
            :disabled="callToAction.disabled"
            :style="computedCallToActionsButtonStyle"
            :class="index < callToActions.length - 1 ? 'mb-2' : ''"
            outlined
            rounded
          >
            {{ callToAction.title }} {{ callToAction.message }}
          </v-btn>
        </template>
      </div>
      <div v-if="Array.isArray(callToActionsForRoute) && callToActionsForRoute.length > 0">
        <template v-for="callToAction in callToActionsForRoute">
          <v-btn
            :key="callToAction.namespace"
            block
            text
            color="primary"
            @click="selectCallToAction(callToAction)"
          >
            {{ callToAction.title }}
          </v-btn>
        </template>
      </div>
      <div
        v-if="Array.isArray(selectedFilters) && selectedFilters.length > 0"
        class="px-4"
      >
        <FilterForm
          @applyFilters="filterList"
          :selected-filters="selectedFilters"
          @remove="removeFilter"
          @removeFilters="removeFilters"
        />
      </div>
      <div
        class="px-4"
        v-if="showBulkActions"
      >
        <v-list-item class="pl-2 pr-0">
          <v-list-item-content>
            {{ Array.isArray(selectedItemsArray) ? selectedItemsArray.length : 0 }} {{ $t('itemsSelected') }}
          </v-list-item-content>
          <v-list-item-content>
            <v-select
              hide-details
              dense
              :label="$t('action')"
              outlined
              :items="whiteListedActions"
              item-text="title"
              return-object
              v-model="selectedBulkOperation"
            />
          </v-list-item-content>
        </v-list-item>
        <v-btn
          v-if="showBulkActions"
          @click="showActionsDialog = true"
          class="mt-2"
          block
          color="primary"
          :disabled="
            typeof selectedBulkOperation === 'undefined' ||
            selectedBulkOperation === null ||
            !Array.isArray(selectedItemsArray) ||
            selectedItemsArray.length < 1
          "
        >
          {{ $t('configureAction') }}
        </v-btn>
      </div>
      <div>
        <v-row dense>
          <v-col v-if="Array.isArray(filters) && filters.length > 0">
            <v-menu
              top
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  block
                  color="blue"
                  :disabled="false"
                  :loading="false"
                  style="text-transform: none !important"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ $t('Filter') }}
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="(filter, index) in filters"
                  :key="index"
                  @click="selectFilter(filter)"
                  link
                >
                  <v-list-item-title>{{ filter.label }}</v-list-item-title>
                  <v-list-item-icon v-if="isFilterSelected(filter)">
                    <v-icon>mdi-check</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          <v-col v-if="importDataValues !== null && importDataValues !== undefined">
            <v-btn
              block
              text
              color="blue"
              style="text-transform: none !important"
              @click="toggleImport"
            >
              {{ $t('import') }}
            </v-btn>
          </v-col>
          <v-col v-if="allowExport">
            <v-btn
              block
              text
              color="blue"
              style="text-transform: none !important"
              @click="exportFilteredListItems"
              :disabled="exporting"
              :loading="exporting"
            >
              {{ $t('export') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <template v-for="(item, index) in listItems">
        <v-list-item
          :key="item.uuid || index"
          class="pa-0"
        >
          <v-checkbox
            v-show="showBulkActions"
            @change="updatedSelectedItemsArray(item)"
            :value="Array.isArray(selectedItemsArray) && selectedItemsArray.some((i) => i && i.uuid === item.uuid)"
            class="pl-4"
          />
          <v-list-item-content class="pa-0">
            <slot
              name="item"
              :item="item"
              :index="index"
              :selectItem="handleItemSelection"
            >
              <!-- Fallback content -->
              {{ item }}
            </slot>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>
    <div
      v-if="hasMoreItems && Array.isArray(listItems) && listItems.length > 0"
      v-intersect.quiet="loadMoreItemsInDom"
    >
      <v-divider />
      <div class="text-center pa-2">
        <v-progress-circular
          v-if="loadingMore"
          indeterminate
          size="24"
          color="primary"
        />
      </div>
    </div>
    <div v-else-if="!Array.isArray(listItems) && processing">
      <template v-for="index in 10">
        <v-skeleton-loader
          ref="skeleton"
          type="list-item-avatar-two-line"
          class="mx-auto"
          :key="index"
        />
        <v-divider
          :key="index + '-div'"
          inset
        />
      </template>
    </div>
    <div
      v-if="Array.isArray(listItems) && listItems.length === 0 && emptyText !== null && !processing"
      class="text-center pa-5 text-body-2"
    >
      <div>{{ emptyText }}</div>
    </div>
    <ActionsDialog
      v-model="showActionsDialog"
      :items="selectedItemsArray"
      :action="selectedBulkOperation"
      @close="closeActionDialog"
    />
  </div>
</template>

<script>
import FilterForm from '@/components/universalFilter/FilterForm'
import ActionsDialog from './ActionsDialog'
import { LRUCache } from '@/utils/LRUCache'

export default {
  name: 'ActionListServerSide',
  components: { ActionsDialog, FilterForm },
  props: {
    filters: {
      type: Array,
      default: () => []
    },
    emptyText: {
      type: String,
      default: () => null
    },
    refreshable: {
      type: Boolean,
      default: () => false
    },
    actions: {
      type: Array,
      default: () => []
    },
    fixedFilters: {
      type: Array,
      default: () => []
    },
    loadItemsCallback: {
      type: Function,
      required: true
    },
    preloadedItemsList: {
      type: Array,
      default: () => null
    },
    callToActions: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: () => false
    },
    showSearch: {
      type: Boolean,
      default: () => true
    },
    importDataValues: {
      type: Object,
      default: null
    },
    preselectedFilter: {
      type: Object,
      default: null
    },
    allowExport: {
      type: Boolean,
      default: true
    },
    pageSize: {
      type: Number,
      default: 50
    },
    searchDebounceMs: {
      type: Number,
      default: 500
    },
    loadItemDetailsCallback: {
      type: Function,
      default: null
    },
    itemCachingEnabled: {
      type: Boolean,
      default: false
    },
    itemCacheMaxSize: {
      type: Number,
      default: 100
    }
  },
  data: () => {
    return {
      listItems: [],
      actionListQuery: null,
      viewId: 'key_' + Math.floor(Math.random() * 100000 + 100000),
      showBulkActions: false,
      showActionsDialog: false,
      selectedItemsArray: [],
      skip: 0,
      selectedBulkOperation: null,
      selectedFilters: [],
      processing: false,
      loadingMore: false,
      hasMoreItems: true,
      searchDebounceTimer: null,
      itemDetailsCache: null,
      exporting: false
    }
  },
  created() {
    this.itemDetailsCache = new LRUCache(this.itemCacheMaxSize)
  },
  methods: {
    toggleImport() {
      this.$store.commit('updateGenericDialog', {
        title: 'Update Generic Dialog',
        component: 'components/common/GenericImport',
        props: {
          importDataValues: this.importDataValues
        }
      })
    },
    updatedSelectedItemsArray(item) {
      if (!Array.isArray(this.selectedItemsArray)) {
        this.selectedItemsArray = [item]
        return
      }

      if (Array.isArray(this.selectedItemsArray)) {
        const index = this.selectedItemsArray.findIndex((i) => i && i.uuid === item.uuid)
        if (index >= 0) {
          this.selectedItemsArray.splice(index, 1)
        } else {
          this.selectedItemsArray.push(item)
        }
      }
    },
    selectCallToAction(callToAction) {
      this.$store.commit('updateSelectedCallToAction', callToAction)
    },
    async exportFilteredListItems() {
      // Prevent multiple simultaneous exports
      if (this.exporting) return

      this.exporting = true
      let allItems = [...this.listItems]

      try {
        // Only fetch more if we have a full page (mod check) AND hasMoreItems is true
        const shouldFetchMore = this.listItems.length % this.pageSize === 0 && this.hasMoreItems

        if (shouldFetchMore) {
          const remainingItems = await this.loadItemsCallback({
            limit: Number.MAX_SAFE_INTEGER, // Get all remaining items
            skip: this.listItems.length,
            conditions: this.queryConditions,
            search: this.actionListQuery || null
          })

          if (Array.isArray(remainingItems)) {
            allItems = [...allItems, ...remainingItems]
          }
        }

        this.$store.commit('updateExportObjects', allItems)
      } catch (error) {
        console.error('Error fetching remaining items for export:', error)
        // Continue with what we have - export partial results
        this.$store.commit('updateExportObjects', allItems)
      } finally {
        this.exporting = false
      }
    },
    // Expose this method for parent components to call
    async exportAllItems() {
      await this.exportFilteredListItems()
    },
    loadItemsByCallback({ limit, skip, conditions, search, append = false }) {
      if (this.loadItemsCallback === undefined || this.loadItemsCallback === null) {
        return Promise.resolve()
      }

      const loadingState = append ? 'loadingMore' : 'processing'
      this[loadingState] = true

      return this.loadItemsCallback({ limit, skip, conditions, search })
        .then((results) => {
          if (!Array.isArray(results)) {
            results = []
          }

          if (append) {
            // Append results for infinite scroll
            this.listItems = [...this.listItems, ...results]
            this.hasMoreItems = results.length === limit
          } else {
            // Replace results for new search/filter
            this.listItems = results
            this.hasMoreItems = results.length === limit
          }

          return results
        })
        .catch((error) => {
          console.error('Error loading items:', error)
          if (!append) {
            this.listItems = []
          }
          this.hasMoreItems = false
          throw error
        })
        .finally(() => {
          this[loadingState] = false
        })
    },
    refreshItems() {
      this.resetAndLoad()
    },
    resetAndLoad() {
      this.skip = 0
      this.hasMoreItems = true
      this.loadItemsByCallback({
        limit: this.pageSize,
        skip: 0,
        conditions: this.queryConditions,
        search: this.actionListQuery || null
      })
    },
    isFilterSelected(filter) {
      return this.selectedFilters.find((selectedFilter) => selectedFilter.key === filter.key)
    },
    selectFilter(filter) {
      if (this.isFilterSelected(filter)) {
        const index = this.selectedFilters.findIndex((selectedFilter) => selectedFilter.key === filter.key)
        this.selectedFilters.splice(index, 1)
      } else {
        this.selectedFilters.push(filter)
      }
      // Reset and reload when filter changes
      this.resetAndLoad()
    },
    removeFilter(index) {
      this.selectedFilters.splice(index, 1)
      this.resetAndLoad()
    },
    removeFilters() {
      this.selectedFilters = []
      this.resetAndLoad()
    },
    toggleAllItems(e) {
      if (e === true) {
        this.selectedItemsArray = [...this.listItems]
      }
      if (e === false) {
        this.selectedItemsArray = []
      }
    },
    getQueryConditionsFromFilters(filters) {
      const queryConditions = JSON.parse(JSON.stringify(filters))

      for (let i in queryConditions) {
        queryConditions[i].property = queryConditions[i].key

        if (queryConditions[i].type === 'number') {
          queryConditions[i].intValue = Number(queryConditions[i].value)
          delete queryConditions[i].value
        } else if (Array.isArray(queryConditions[i].value) && queryConditions[i].operator !== 'between') {
          queryConditions[i].values = queryConditions[i].value
          delete queryConditions[i].items
          delete queryConditions[i].value
        } else if (Array.isArray(queryConditions[i].value) && queryConditions[i].operator === 'between') {
          queryConditions[i].floatValues = queryConditions[i].value
          delete queryConditions[i].items
          delete queryConditions[i].value
        }

        delete queryConditions[i].key
        delete queryConditions[i].label
        delete queryConditions[i].type
      }

      if (Array.isArray(this.fixedFilters) && this.fixedFilters.length > 0) {
        const clonedFixedFilters = JSON.parse(JSON.stringify(this.fixedFilters))
        for (let i in clonedFixedFilters) {
          const fixedFilter = clonedFixedFilters[i]
          fixedFilter.property = fixedFilter.key
          delete fixedFilter.key
          delete fixedFilter.type
          queryConditions.push(fixedFilter)
        }
      }

      return queryConditions
    },
    filterList(filters) {
      this.selectedFilters = filters || []
      this.resetAndLoad()
    },
    closeActionDialog() {
      this.toggleAllItems(false)
      this.showActionsDialog = false
    },
    loadMoreItemsInDom() {
      if (this.loadingMore || !this.hasMoreItems) {
        return
      }

      this.skip = this.listItems.length
      this.loadingMore = true
      this.loadItemsByCallback({
        limit: this.pageSize,
        skip: this.skip,
        conditions: this.queryConditions,
        search: this.actionListQuery || null,
        append: true
      })
    },
    onSearchInput() {
      // Debounce search input
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.resetAndLoad()
      }, this.searchDebounceMs)
    },
    refresh() {
      this.resetAndLoad()
    },
    getItemKey(item) {
      if (!item) {
        return null
      }

      if (item.uuid) return item.uuid
      if (item.id) return item.id
      if (item._id) return item._id
      if (item.slug) return item.slug

      try {
        return JSON.stringify(item)
      } catch (e) {
        return null
      }
    },
    handleItemSelection(item) {
      if (!item) {
        return
      }

      if (this.itemCachingEnabled) {
        const itemKey = this.getItemKey(item)
        if (itemKey && this.itemDetailsCache.has(itemKey)) {
          const cachedDetails = this.itemDetailsCache.get(itemKey)
          this.$emit('item-selected', cachedDetails || item)
          return
        }
      }

      if (!this.loadItemDetailsCallback) {
        this.$emit('item-selected', item)
        return
      }

      this.loadItemDetails(item)
    },
    loadItemDetails(item) {
      if (!this.loadItemDetailsCallback) {
        return Promise.resolve()
      }

      return this.loadItemDetailsCallback(item)
        .then((fullDetails) => {
          const details = fullDetails || null

          if (this.itemCachingEnabled && details) {
            const itemKey = this.getItemKey(item)
            if (itemKey) {
              this.itemDetailsCache.set(itemKey, details)

              const itemIndex = this.listItems.findIndex((listItem) => {
                const listItemKey = this.getItemKey(listItem)
                return listItemKey === itemKey
              })
              if (itemIndex >= 0 && details) {
                this.$set(this.listItems, itemIndex, { ...this.listItems[itemIndex], ...details })
              }
            }
          }

          this.$emit('item-selected', details || item)

          return details
        })
        .catch((error) => {
          console.error('Error loading item details:', error)
          this.$emit('item-selected', item)
          throw error
        })
    }
  },
  computed: {
    computedCallToActionsButtonStyle() {
      const primaryColor = this.primaryColor
      return {
        backgroundColor: `${primaryColor}20`,
        borderColor: primaryColor,
        color: primaryColor
      }
    },
    primaryColor() {
      return this.$store.getters.primaryColor
    },
    callToActionsForRoute() {
      if (this.$store.state.callToActions === null || this.$store.state.callToActions === undefined) {
        return []
      }

      return this.$store.state.callToActions.filter((object) => Array.isArray(object.routes) && object.routes.includes(this.$route.path))
    },
    queryConditions() {
      return this.getQueryConditionsFromFilters(this.selectedFilters)
    },
    organizationUUID() {
      return this.$store.state.organization.uuid
    },
    showFilters: {
      get() {
        return this.$store.state.showFilters
      },
      set(value) {
        this.$store.commit('updateShowFilters', value)
      }
    },
    userIsAdmin() {
      return !!this.$store.state.user.email.includes('@salescloud.is')
    },
    whiteListedActions() {
      if (this.userIsAdmin) {
        return this.actions
      }
      return this.actions.filter((action) => action.whiteListed === true)
    },
    actionSuccess() {
      return this.$store.state.actionSuccess
    },
    actionError() {
      return this.$store.state.actionError
    }
  },
  watch: {
    preloadedItemsList(newValue) {
      if (Array.isArray(newValue)) {
        this.listItems = newValue
        this.hasMoreItems = false // Preloaded items are complete
      }
    },
    actionSuccess(newValue) {
      if (newValue) {
        this.resetAndLoad()
      }
    },
    actionError(newValue) {
      if (newValue) {
        this.resetAndLoad()
      }
    }
  },
  mounted() {
    this.processing = this.loading

    if (this.preloadedItemsList !== null && Array.isArray(this.preloadedItemsList)) {
      this.listItems = this.preloadedItemsList
      this.hasMoreItems = false
    } else if (this.loadItemsCallback !== null && this.loadItemsCallback !== undefined) {
      if (typeof this.preselectedFilter !== 'undefined' && this.preselectedFilter !== null) {
        this.selectFilter(this.preselectedFilter)
      } else {
        this.resetAndLoad()
      }
    }
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
    if (this.itemDetailsCache) {
      this.itemDetailsCache.clear()
    }
  }
}
</script>

<style>
.fixed-bar {
  position: sticky;
  position: -webkit-sticky; /* for Safari */
  top: inherit;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  z-index: 3;
}
</style>
